"use client";

import { PersonalDataFormSchema } from "@features/auth/types";
import {
  deleteFileFromStorage,
  getFileFromStorage,
  saveFileToStorage,
} from "@lib/utils/base64/file";

export const FORM_NAME = "create-user-form";
export const BUSINESS_LOGO_KEY = "business-logo-file";
export const PLAN_SELECTION_KEY = "signup-plan-selection";
export const SAVE_DEBOUNCE_MS = 500;

export function resolveOnboardingBusinessEmail(
  savedBusinessEmail: string | null | undefined,
  authEmail: string | null | undefined,
): string | undefined {
  return savedBusinessEmail || authEmail || undefined;
}

export const saveFormData = async (
  values: Partial<PersonalDataFormSchema>,
): Promise<void> => {
  if (values.businessLogoImage) {
    await saveFileToStorage(BUSINESS_LOGO_KEY, values.businessLogoImage);
  }

  const { businessLogoImage, ...textFields } = values;
  localStorage.setItem(
    FORM_NAME,
    JSON.stringify({
      ...textFields,
      hasBusinessLogo: !!businessLogoImage,
    }),
  );
};

export const clearFormData = (): void => {
  localStorage.removeItem(FORM_NAME);
  try {
    deleteFileFromStorage(BUSINESS_LOGO_KEY);
  } catch {
    // File may not exist - it's okay
  }
};

export const getFormData = async () => {
  try {
    const savedDataJSON = localStorage.getItem(FORM_NAME);
    if (!savedDataJSON) return {};

    const { hasBusinessLogo, ...savedData } = JSON.parse(savedDataJSON);

    if (hasBusinessLogo) {
      const file = await getFileFromStorage(BUSINESS_LOGO_KEY);
      savedData.businessLogoImage = file;
    }

    return savedData;
  } catch {
    return {};
  }
};

export type SignupPlanSelection = {
  plan: "basics" | "pro";
  billing: "monthly" | "annual";
};

export const savePlanSelection = (selection: SignupPlanSelection): void => {
  localStorage.setItem(PLAN_SELECTION_KEY, JSON.stringify(selection));
};

export const getPlanSelection = (): SignupPlanSelection | null => {
  try {
    const value = JSON.parse(
      localStorage.getItem(PLAN_SELECTION_KEY) ?? "null",
    );
    if (
      (value?.plan === "basics" || value?.plan === "pro") &&
      (value?.billing === "monthly" || value?.billing === "annual")
    ) {
      return value as SignupPlanSelection;
    }
    return null;
  } catch {
    return null;
  }
};

export const clearPlanSelection = (): void => {
  localStorage.removeItem(PLAN_SELECTION_KEY);
};

// ─── Founding Chef helpers ────────────────────────────────────────────────────

export const FOUNDING_CHEF_KEY = "founding-chef";

// ─── Meal Prep helpers ────────────────────────────────────────────────────────

export const MEAL_PREP_KEY = "meal-prep";

export interface FoundingChefData {
  token: string;
}

export const saveFoundingChefData = (data: FoundingChefData): void => {
  localStorage.setItem(FOUNDING_CHEF_KEY, JSON.stringify(data));
};

export const getFoundingChefData = (): FoundingChefData | null => {
  try {
    const raw = localStorage.getItem(FOUNDING_CHEF_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as FoundingChefData;
  } catch {
    return null;
  }
};

export const clearFoundingChefData = (): void => {
  localStorage.removeItem(FOUNDING_CHEF_KEY);
};

export interface MealPrepData {
  token: string;
}

export const saveMealPrepData = (data: MealPrepData): void => {
  localStorage.setItem(MEAL_PREP_KEY, JSON.stringify(data));
};

export const getMealPrepData = (): MealPrepData | null => {
  try {
    const raw = localStorage.getItem(MEAL_PREP_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as MealPrepData;
  } catch {
    return null;
  }
};

export const clearMealPrepData = (): void => {
  localStorage.removeItem(MEAL_PREP_KEY);
};
